// Pure logic for the ReviewQueue N-up per-candidate review page. Kept separate
// from the React view so grouping, the per-candidate rating<->vote mapping, the
// PENDING/authorization lifecycle, and the reset request are unit-testable without
// rendering.
//
// #584 triple-review rework (rq-triple-review-contract.md): the A/B/EQUAL
// comparative verdict is RETIRED as the data model (AD-1). Each candidate
// ReviewItem is rated INDEPENDENTLY (usable / not-usable, mapped onto the existing
// ReviewVote decision APPROVE/REJECT + isBest), editable only while the item is
// PENDING and the caller is authorized for the question (AD-2/AD-3), read-only
// after finalize until an admin reset reopens the whole question (AD-3/AD-6).

// The independent per-candidate rating. Maps onto the existing ReviewVote decision
// so no schema change is needed (AD-1): USABLE↔APPROVE, NOT_USABLE↔REJECT.
export const RATING = {
  USABLE: "USABLE",
  NOT_USABLE: "NOT_USABLE",
};

// Dimensions a reviewer can tag to make "why" concrete rather than generic.
export const DIMENSIONS = [
  "accuracy",
  "labeling",
  "clarity",
  "artifacts",
  "relevance",
];

function candidateSortKey(item) {
  const p = item.payload || {};
  return String(p.candidateId || item.id || "");
}

// Group the flat queue (one item per candidate) into per-question sets, keyed by
// subjectKey (=questionId). Candidates inside a group are ordered by candidateId
// (falling back to item id) so cell order is stable across reloads. The whole
// candidate set is kept — AD-5 returns ALL candidates (decided included); the view
// renders each in its own cell (no top-2 slice).
export function groupBySubject(items) {
  const bySubject = new Map();
  (items || []).forEach((item) => {
    const key = item.subjectKey;
    if (!bySubject.has(key)) bySubject.set(key, []);
    bySubject.get(key).push(item);
  });
  const groups = [];
  bySubject.forEach((candidates, subjectKey) => {
    const ordered = [...candidates].sort((x, y) => {
      const xk = candidateSortKey(x);
      const yk = candidateSortKey(y);
      return xk < yk ? -1 : xk > yk ? 1 : 0;
    });
    // Every candidate shares the question's visualKind and its (per-question)
    // review assignment — fold both onto the group for the view.
    const visualKind = ordered.find((c) => c && c.visualKind)?.visualKind || null;
    const assignment = ordered.find((c) => c && c.assignment)?.assignment || null;
    const heldReason = ordered.find((c) => c && c.heldReason)?.heldReason || null;
    // S3-U1: reviewer-visible grounding block + admin-only groundingStatus are QUESTION-level
    // on QuestionCandidatesDto — fold them onto the group (stamped per-candidate by
    // flattenQuestionsResponse, exactly like assignment/heldReason).
    const grounding = ordered.find((c) => c && c.grounding)?.grounding || null;
    const groundingStatus = ordered.find((c) => c && c.groundingStatus)?.groundingStatus || null;
    groups.push({ subjectKey, candidates: ordered, visualKind, assignment, heldReason, grounding, groundingStatus });
  });
  return groups;
}

// --- Visual-kind helpers (retained from the compare page) ---

// Human labels for the VisualKind names the backend sends (VisualKindClassifier).
export const VISUAL_KIND_LABELS = {
  IMAGE: "Image",
  TABLE_MD: "Table",
  TABLE_IMAGE: "Table (image)",
  DIAGRAM: "Diagram",
  NONE: "No visual",
  UNKNOWN: "Needs classification",
};

export function visualKindLabel(kind) {
  return VISUAL_KIND_LABELS[kind] || "Needs classification";
}

// The VisualKind names the frontend knows, in display order. UNKNOWN is the catch-all.
export const KNOWN_VISUAL_KINDS = ["IMAGE", "TABLE_MD", "TABLE_IMAGE", "DIAGRAM", "NONE", "UNKNOWN"];

// A known, tutor-facing visual kind: present AND not the UNKNOWN catch-all.
export function isKnownVisualKind(kind) {
  return Boolean(kind) && normalizeVisualKind(kind) !== "UNKNOWN";
}

// Whether to render the visual-kind tag/chip for a viewer. A known kind shows for
// everyone; UNKNOWN is admin-only chrome; an absent kind shows for no one.
export function showVisualKind(kind, isAdmin) {
  return isKnownVisualKind(kind) || Boolean(isAdmin && kind);
}

// Normalize a group's kind to a KNOWN bucket: null/absent OR a future/unrecognized
// backend enum value → UNKNOWN.
export function normalizeVisualKind(kind) {
  return KNOWN_VISUAL_KINDS.includes(kind) ? kind : "UNKNOWN";
}

// S1.2/P2-1: what the RAIL row shows for a kind. Known kinds keep their lowercase label;
// an admin-visible UNKNOWN collapses to a COMPACT marker ({ compact: true } → a "?" glyph
// carrying the "Needs classification" title) so ten unknown rows don't drown the list in
// repeated "needs classification" text. The question view keeps the full label (filter
// chips / admin chrome) — this helper is rail-only. Null = render nothing (tutor
// UNKNOWN/absent, unchanged).
export function railKindDisplay(kind, isAdmin) {
  if (!showVisualKind(kind, isAdmin)) return null;
  if (normalizeVisualKind(kind) === "UNKNOWN") return { compact: true };
  return { text: visualKindLabel(kind).toLowerCase() };
}

// Distinct visualKinds present across the groups (for building the filter row).
export function presentVisualKinds(groups) {
  const present = new Set((groups || []).map((g) => normalizeVisualKind(g.visualKind)));
  return KNOWN_VISUAL_KINDS.filter((k) => present.has(k));
}

// Filter groups to a kind; "ALL"/null → unfiltered.
export function filterGroupsByKind(groups, kind) {
  if (!kind || kind === "ALL") return groups || [];
  return (groups || []).filter((g) => normalizeVisualKind(g.visualKind) === kind);
}

// --- Per-candidate rating model (AD-1) ---

// USABLE↔APPROVE, NOT_USABLE↔REJECT (reuses the existing ReviewVote decision so no
// schema change is required — the comparative verdict is gone, the vote stays).
export function ratingDecision(rating) {
  if (rating === RATING.USABLE) return "APPROVE";
  if (rating === RATING.NOT_USABLE) return "REJECT";
  return null;
}

export function ratingFromDecision(decision) {
  if (decision === "APPROVE") return RATING.USABLE;
  if (decision === "REJECT") return RATING.NOT_USABLE;
  return null; // NEUTRAL / unknown → no explicit usable/not-usable rating
}

export function ratingLabel(rating) {
  return rating === RATING.USABLE ? "Usable" : rating === RATING.NOT_USABLE ? "Not usable" : "";
}

// The caller's saved rating for a candidate, derived from their own vote on the
// item — { rating, isBest, comment } — or null when they haven't rated it.
export function savedRating(candidate) {
  const v = candidate && candidate.myVote;
  if (!v) return null;
  return {
    rating: ratingFromDecision(v.decision),
    isBest: Boolean(v.isBest),
    comment: v.comment || "",
    // P2-8: the caller's OWN danger flag on this candidate, so the admin ⚑ toggle reflects
    // saved state (server projects it into myVote.dangerFlag; the cross-reviewer aggregate is separate).
    dangerFlag: Boolean(v.dangerFlag),
  };
}

// Build the per-candidate vote-upsert body (POST /qlearn/review/item/{id}/vote).
// The comment field is omitted when empty so a blank note never clobbers a prior one.
// P2-8: dangerFlag rides the SAME body (server maps it into typeSignal, symmetric with the
// comparative decision's stampGroupSignal). Sent explicitly true/false so an un-toggle clears it.
export function ratingToVoteBody(state) {
  const s = state || {};
  const body = { decision: ratingDecision(s.rating), isBest: Boolean(s.isBest) };
  const comment = (s.comment || "").trim();
  if (comment) body.comment = comment;
  if (s.dangerFlag) {
    body.dangerFlag = true;
    const dn = (s.dangerNote || "").trim();
    if (dn) body.dangerNote = dn;
  }
  return body;
}

// S4/T-R4: how many of a group's candidates carry an active (admin-only) danger flag.
// Admin surfaces only — tutors never receive the aggregate dangerFlagged field.
export function countDangerFlagged(group) {
  const cands = (group && group.candidates) || [];
  return cands.reduce((n, c) => n + (c && c.dangerFlagged ? 1 : 0), 0);
}

// P0-1d: is the pick-best WINNER one the reviewer has danger-flagged in-flight? Drives the
// decision-time confirm warning ("you are approving an image you flagged"). Uses the reviewer's
// OWN in-flight flag set, not server state.
export function winnerDangerFlagged(selectedId, dangerFlagIds) {
  if (!selectedId) return false;
  if (dangerFlagIds && typeof dangerFlagIds.has === "function") return dangerFlagIds.has(selectedId);
  return Array.isArray(dangerFlagIds) && dangerFlagIds.includes(selectedId);
}

// --- Lifecycle + authorization (AD-2 / AD-3, fail-closed) ---

// A candidate is editable only while it is PENDING. Absent status == PENDING (a
// pre-decision item), so it stays editable; any other status is finalized.
export function isItemPending(candidate) {
  const s = candidate && candidate.reviewStatus;
  return !s || s === "PENDING";
}

export function isItemFinalized(candidate) {
  const s = candidate && candidate.reviewStatus;
  return Boolean(s) && s !== "PENDING";
}

// AD-2: authorization to rate is per-question (subjectKey) reviewer assignment,
// FAIL-CLOSED. An admin may always rate. For a tutor the authoritative signal is the
// server-computed boolean `assignment.canRate` on QuestionCandidatesDto (reconciled
// against QLearn-service#579): tutors never receive `assignedReviewerIds` (admin-only,
// plural), so client-side identity matching is neither possible nor needed. No
// assignment info, or canRate truly absent with no legacy signal → NOT authorized
// (read-only). Mentorship is explicitly NOT consulted here.
export function isAuthorizedToRate(group, ctx) {
  const c = ctx || {};
  if (c.isAdmin) return true;
  const a = (group && group.assignment) || null;
  if (!a) return false; // fail-closed
  // REAL backend signal (#579): server-computed per-caller boolean.
  if (typeof a.canRate === "boolean") return a.canRate;
  // Back-compat with earlier assumed shapes (pre-reconcile), harmless if unused.
  if (typeof a.authorized === "boolean") return a.authorized;
  if (a.assignedReviewerId != null) {
    return Boolean(a.active && String(a.assignedReviewerId) === String(c.viewerId));
  }
  return false; // canRate absent and no legacy signal → fail-closed
}

// Whether the question is assigned to the caller (for the "assigned to me" display,
// distinct from canRate which governs write access). Admins see all; tutors read the
// server's `assignment.assignedToMe`.
export function isAssignedToMe(group, ctx) {
  const c = ctx || {};
  if (c.isAdmin) return true;
  const a = (group && group.assignment) || null;
  return Boolean(a && a.assignedToMe);
}

// Editable iff the item is PENDING AND the caller is authorized for the question.
export function isCandidateEditable(candidate, group, ctx) {
  return isItemPending(candidate) && isAuthorizedToRate(group, ctx);
}

// The id of the candidate the caller flagged best (isBest is ≤1 per question,
// enforced server-side; this reads back the current pick), or null.
export function bestItemId(group) {
  const cands = (group && group.candidates) || [];
  const best = cands.find((c) => {
    const s = savedRating(c);
    return s && s.isBest;
  });
  return best ? best.id : null;
}

// --- Reset (AD-3 / AD-6) ---

// Body for POST /qlearn/review/questions/{subjectKey}/reset.
export function buildResetRequest(reason) {
  return { confirm: true, reason: (reason || "").trim() };
}

// Classify the reset response. AD-6: a 409 means a candidate is PUBLISHED/RETRACTED
// so the question can't be reset — surface it non-destructively, do NOT reopen. 404
// is the flag-dark case; any other error is a transient failure.
export function resetOutcome(res) {
  if (res && res.errStatus) {
    const s = String(res.errStatus);
    if (s === "409") return "conflict";
    if (s === "404") return "dark";
    return "error";
  }
  return "ok";
}

// --- Adapters for the all-candidates reads (AD-5, contract §5), reconciled to the
// REAL QLearn-service#579 DTOs ---
// GET /qlearn/review/questions/{subjectKey} → QuestionCandidatesDto → a group:
//   { subjectKey, reviewType, candidates: ReviewItemDto[],
//     assignment: { assignedToMe, canRate, assignedReviewerIds? (ADMIN-only) } }
// ReviewItemDto carries visualKind PER-CANDIDATE (group kind is derived from the
// candidates by groupBySubject, not a top-level field) and myVote { decision, isBest,
// isDuplicate?, keeperQuestionId? } | null. Tolerant: also accepts `items` instead of
// `candidates`, a bare array, and a legacy top-level `assignment`.
export function adaptQuestionResponse(res) {
  if (!res) return null;
  const subjectKey = res.subjectKey || res.questionId || null;
  const list = Array.isArray(res) ? res : res.candidates || res.items || [];
  const normalized = list.map((c) => ({ ...c, subjectKey: (c && c.subjectKey) || subjectKey }));
  const g =
    groupBySubject(normalized)[0] || { subjectKey, candidates: [], visualKind: null, assignment: null };
  // Assignment is GROUP-level on QuestionCandidatesDto — prefer it over any per-item
  // fold (real ReviewItemDto carries no assignment).
  if (!Array.isArray(res) && res.assignment) g.assignment = res.assignment;
  // S3-U1: single-question reads carry grounding/groundingStatus at the top level too.
  if (!Array.isArray(res) && res.grounding) g.grounding = res.grounding;
  if (!Array.isArray(res) && res.groundingStatus) g.groundingStatus = res.groundingStatus;
  if (subjectKey) g.subjectKey = subjectKey;
  return g;
}

// Normalize the queue LIST response to flat candidate items (the shape the page's
// grouping pipeline consumes), stamping the GROUP-level assignment onto each of a
// question's candidates so authorization survives the flattening. Handles BOTH the
// real QuestionCandidatesPage { questions: QuestionCandidatesDto[], totalElements }
// and the legacy flat { items: ReviewItemDto[], totalElements } shape.
export function flattenQuestionsResponse(res) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return { items: [], totalElements: 0 };
  if (Array.isArray(res.questions)) {
    const items = res.questions.flatMap((q) =>
      ((q && q.candidates) || []).map((c) => ({
        ...c,
        subjectKey: (c && c.subjectKey) || (q && q.subjectKey),
        // Group-level assignment stamped onto each candidate so groupBySubject can
        // fold it back (real ReviewItemDto has no per-item assignment).
        assignment: (c && c.assignment) || (q && q.assignment) || null,
        // A1 hold marker (admin-only server field) rides the same stamp-and-fold path.
        heldReason: (q && q.heldReason) || null,
        // S3-U1 grounding (question-level): reviewer-visible references + admin-only status
        // ride the same stamp-and-fold path so groupBySubject can lift them to the group.
        grounding: (c && c.grounding) || (q && q.grounding) || null,
        groundingStatus: (c && c.groundingStatus) || (q && q.groundingStatus) || null,
      }))
    );
    return { items, totalElements: res.totalElements != null ? res.totalElements : items.length };
  }
  if (Array.isArray(res.items)) {
    return { items: res.items, totalElements: res.totalElements || 0 };
  }
  return { items: [], totalElements: 0 };
}

// Whether the server holds more QUESTIONS than this page returned — unit-safe per shape.
// On the QuestionCandidatesPage shape, totalElements counts QUESTIONS, so it must be
// compared to questions.length — never to the flattened candidate-card count (a page of
// multi-candidate questions would otherwise mask unfetched questions: 4 questions × 3
// candidates loaded of totalElements=10 → 10 > 12 reads "all loaded", wrongly). On the
// legacy flat shape, totalElements counts ITEMS and compares to items.length.
export function moreQuestionsOnServer(res) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return false;
  if (Array.isArray(res.questions)) {
    return res.totalElements != null && res.totalElements > res.questions.length;
  }
  if (Array.isArray(res.items)) {
    return (res.totalElements || 0) > res.items.length;
  }
  return false;
}

// True when the response carries a recognizable candidate payload (either shape) —
// used to distinguish a real empty/valid page from an error/dark response.
export function hasQuestionsPayload(res) {
  return Boolean(res && (Array.isArray(res.items) || Array.isArray(res.questions)));
}

// --- Needs-generation partition (#584 S1-U1) ---
// The admin-only held-group partition on QuestionCandidatesPage: an array of
// { subjectKey, pendingCount, floor, topUpRequest: { status, neededCount, requestedAt } }.
// Tutors receive NO needsGeneration key (server sends null; the redaction choke-point also
// drops it), so this yields [] for them — the rail section never renders.
export function extractNeedsGeneration(res) {
  if (!res || typeof res !== "object" || Array.isArray(res)) return [];
  return Array.isArray(res.needsGeneration) ? res.needsGeneration : [];
}

// A human label for a held group's top-up request state (admin chrome). The server owns the
// status; the UI only renders it. OPEN → a request is out to the generation pipeline;
// CONSUMED → the pipeline took the last request but the group is short again; NONE → no
// request landed yet (fail-open emission retries on the next read).
export function topUpStatusLabel(topUpRequest) {
  const t = topUpRequest || {};
  const status = t.status || "NONE";
  if (status === "OPEN") {
    return t.neededCount ? `top-up requested (+${t.neededCount})` : "top-up requested";
  }
  if (status === "CONSUMED") return "top-up consumed — awaiting re-request";
  return "no top-up request yet";
}

// --- Group state: work vs completed (AD-5 keeps decided candidates visible) ---

export const GROUP_STATE = {
  TO_REVIEW: "TO_REVIEW",
  COMPLETED: "COMPLETED",
};

// Group state is DERIVED per-candidate (candidates finalize independently):
//   TO_REVIEW — ANY candidate still PENDING (`partial` marks a decided sibling).
//   COMPLETED — no candidate PENDING; status by precedence
//               RETRACTED > PUBLISHED > APPROVED (any) > REJECTED (all rejected).
export function groupReviewState(group) {
  const cands = ((group && group.candidates) || []).filter(Boolean);
  if (cands.length === 0) {
    return { state: GROUP_STATE.COMPLETED, status: null, partial: false };
  }
  const anyPending = cands.some((c) => isItemPending(c));
  const anyDecided = cands.some((c) => isItemFinalized(c));
  if (anyPending) {
    return { state: GROUP_STATE.TO_REVIEW, status: null, partial: anyDecided };
  }
  let status;
  if (cands.some((c) => c.publishStatus === "RETRACTED")) status = "RETRACTED";
  else if (cands.some((c) => c.publishStatus === "PUBLISHED")) status = "PUBLISHED";
  else if (cands.some((c) => c.reviewStatus === "APPROVED")) status = "APPROVED";
  else status = "REJECTED";
  return { state: GROUP_STATE.COMPLETED, status, partial: false };
}

// A fully-decided question is read-only (no candidate is PENDING); only an admin
// reset can reopen it.
export function isGroupReadOnly(group) {
  return groupReviewState(group).state === GROUP_STATE.COMPLETED;
}

// Partition loaded groups into still-reviewable work and completed (decided) sets.
export function splitGroupsByState(groups) {
  const work = [];
  const completed = [];
  (groups || []).forEach((g) => {
    if (groupReviewState(g).state === GROUP_STATE.TO_REVIEW) work.push(g);
    else completed.push(g);
  });
  return { work, completed };
}

// A compact rail glyph: the reviewer has rated at least one candidate → ✓, else null.
export function myRatingGlyph(group) {
  const cands = (group && group.candidates) || [];
  return cands.some((c) => savedRating(c)) ? "✓" : null;
}

// MIXED-DEPLOY GUARD: the rail's DECIDED fetch may hit an old backend that ignores
// the status param and answers a PENDING page. Drop pending strays (derive from each
// item's OWN reviewStatus) and dedupe anything already in the work queue by id.
export function filterDecidedItems(items, workQueueIds) {
  const seen = workQueueIds instanceof Set ? workQueueIds : new Set(workQueueIds || []);
  return (items || []).filter(
    (it) => it && it.reviewStatus && it.reviewStatus !== "PENDING" && !seen.has(it.id)
  );
}

// --- Suitability (retained: per-question "no visual belongs here") ---

export function buildSuitabilityRequest(subjectKey, examType, state) {
  const s = state || {};
  const body = { questionId: subjectKey };
  if (examType) body.examType = examType;
  if (s.image !== undefined) {
    body.unsuitableForImage = s.image;
    const r = (s.imageReason || "").trim();
    if (s.image && r) body.imageReason = r;
  }
  if (s.table !== undefined) {
    body.unsuitableForTable = s.table;
    const r = (s.tableReason || "").trim();
    if (s.table && r) body.tableReason = r;
  }
  return body;
}

export function suitabilityOutcome(res) {
  if (res && res.errStatus) {
    return String(res.errStatus) === "404" ? "dark" : "error";
  }
  return "ok";
}

// --- Admin reviewer-assignment panel (WI-2, AD-2) — pure helpers ---

// The admin-only current-assignee list for a question: the server-returned
// assignment.assignedReviewerIds (admin-only; tutors never receive it). Null/absent
// (a non-admin payload, or no assignment) → empty list. De-duped, string-normalized.
export function currentAssigneeIds(group) {
  const a = (group && group.assignment) || null;
  const ids = a && Array.isArray(a.assignedReviewerIds) ? a.assignedReviewerIds : [];
  const seen = new Set();
  const out = [];
  ids.forEach((id) => {
    const s = id == null ? "" : String(id);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  });
  return out;
}

// Normalize the GET /qlearn/users?role=TUTOR roster response to a stable
// { id, displayName, email } list. Accepts a bare User[] (the real shape), a
// { users } / { userList } wrapper, tolerates missing name/email, and returns [] for
// an error/dark response ({ status }). Rows without an id are dropped (unassignable).
export function normalizeRoster(res) {
  if (!res || (typeof res === "object" && !Array.isArray(res) && res.status)) return [];
  const list = Array.isArray(res) ? res : res.users || res.userList || [];
  return (list || [])
    .filter((u) => u && (u.id != null))
    .map((u) => ({
      id: String(u.id),
      displayName: u.displayName || u.name || "",
      email: u.email || "",
    }));
}

// Case-insensitive search over a roster by display name OR email. Blank query → all.
// UUID is intentionally NOT matched (the picker is by name/email, per the contract).
export function searchRoster(roster, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return roster || [];
  return (roster || []).filter((u) => {
    const name = String(u.displayName || "").toLowerCase();
    const email = String(u.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });
}

// A human label for an assignee id, resolved through the roster. Falls back to a short
// id tail when the roster hasn't loaded or the user isn't in it (revoke still works by id).
export function assigneeLabel(reviewerId, roster) {
  const id = reviewerId == null ? "" : String(reviewerId);
  const u = (roster || []).find((x) => x && String(x.id) === id);
  if (u && (u.displayName || u.email)) {
    return u.email ? `${u.displayName || u.email} (${u.email})` : u.displayName;
  }
  return id.length > 8 ? `…${id.slice(-6)}` : id || "unknown";
}

// Classify an assign/revoke response: 404 → dark (feature off), any other errStatus →
// error, else ok. Mirrors resetOutcome so the panel surfaces failures non-destructively.
export function assignmentOutcome(res) {
  if (res && res.errStatus) {
    return String(res.errStatus) === "404" ? "dark" : "error";
  }
  return "ok";
}

// --- Comparative group decision (#584 A2, T-CMP U1) — pure helpers ---

// The structured reject-reason categories (server enum, T-CMP B1). Labels map the
// retired per-candidate chips onto the group decision.
export const REJECT_CATEGORIES = [
  { key: "ACCURACY", label: "Accuracy" },
  { key: "LABELING", label: "Labeling" },
  { key: "CLARITY", label: "Clarity" },
  { key: "ARTIFACT", label: "Artifacts" },
  { key: "RELEVANCE", label: "Relevance" },
  // S5/P0-2/A4: the answer-leakage class — the failure human review exists to catch (a
  // stem-placed image that gives away the answer). `hint` is the DIMENSION_HINTS line the reject
  // chip carries as a tooltip. Must stay in sync with the backend REJECT_CATEGORIES set.
  { key: "ANSWER_LEAKAGE", label: "Answer leakage", hint: "Does not reveal the correct answer when shown pre-answer" },
  { key: "OTHER", label: "Other" },
];

// Classify a decideReviewQuestion response: 404 dark, 409 conflict (already decided —
// reload), other errStatus -> error, else ok. Mirrors assignmentOutcome.
export function decisionOutcome(res) {
  if (res && res.errStatus) {
    const s = String(res.errStatus);
    if (s === "404") return "dark";
    if (s === "409") return "conflict";
    return "error";
  }
  return "ok";
}

// Build the decision request body (T-CMP B1 shape). dangerFlagIds: Set/array of item
// ids the tutor flagged. Returns null when the inputs cannot form a valid decision —
// the UI disables confirm on the same predicate, this is the belt-and-suspenders.
export function buildGroupDecision({ type, winnerId, category, note, dangerFlagIds }) {
  const dangerFlags = [...(dangerFlagIds || [])].map((itemId) => ({ itemId }));
  if (type === "PICK_BEST") {
    if (!winnerId) return null;
    return { type, winnerId, dangerFlags };
  }
  if (type === "REJECT_SET") {
    const cat = (category || "").trim();
    const line = (note || "").trim();
    if (!cat || !line || line.length > 200) return null;
    return { type, reason: { category: cat, note: line }, dangerFlags };
  }
  return null;
}

// --- S5/P0-2/A4: placement + answer-leakage model (pure helpers) ---

// Where a candidate image is shown to the student. STEM = with the question before answering
// (must not reveal the answer); EXPLANATION = in the post-answer explanation (the leak-safe
// default). Server stamps an explicit value at ingest and surfaces EXPLANATION for legacy items.
export const PLACEMENT = { STEM: "STEM", EXPLANATION: "EXPLANATION" };

// One candidate's placement, defaulting leak-safe: only an explicit STEM is STEM; absent/blank/
// unrecognized folds to EXPLANATION (matches the server's isStem-vs-default semantics).
export function candidatePlacement(candidate) {
  const raw = String(candidate?.payload?.placement || "").trim().toUpperCase();
  return raw === PLACEMENT.STEM ? PLACEMENT.STEM : PLACEMENT.EXPLANATION;
}

// The group's effective placement for the reviewer banner. Candidates share a question, so they
// normally agree; if they DISAGREE, show STEM (leak-safe dominance — any STEM makes the group
// leak-risky). Equivalent to "the first candidate's effective placement" when they all agree.
export function groupPlacement(group) {
  const cands = (group?.candidates || []).filter(Boolean);
  return cands.some((c) => candidatePlacement(c) === PLACEMENT.STEM)
    ? PLACEMENT.STEM
    : PLACEMENT.EXPLANATION;
}

// Do a group's candidates disagree on placement? Admin-only surface (the metadata panel shows
// each candidate's own placement, so a mix is visible there); the banner uses leak-safe dominance.
export function placementMixed(group) {
  const cands = (group?.candidates || []).filter(Boolean);
  if (cands.length < 2) return false;
  return new Set(cands.map(candidatePlacement)).size > 1;
}

// --- S3-U1 / S2-addendum D4 + R-V4: source-image grounding (pure helpers) ---

// Reference cap per question (R-DELTA-P2-1, config default 3). The backend enforces the
// cap at both link paths; the UI mirrors it belt-and-suspenders so a mis-emitted overflow
// never blows out the strip.
export const REFERENCE_CAP = 3;

// The derived grounding-state closed set (R-V4-A2). ADMIN-ONLY (the pipeline-compliance
// state) — the DTO only carries groundingStatus for admin callers; tutors never receive it.
export const GROUNDING_STATUS = {
  GROUNDED: "GROUNDED",
  UNGROUNDED: "UNGROUNDED",
  UNKNOWN_LEGACY: "UNKNOWN_LEGACY",
};

// The question's ACTIVE reference assets, capped at REFERENCE_CAP. Reviewer-visible for BOTH
// roles (the ground truth a reviewer judges anatomical fidelity against — D1). Null-safe.
export function groundingReferences(group) {
  const refs = (group && group.grounding && group.grounding.references) || [];
  return refs.filter(Boolean).slice(0, REFERENCE_CAP);
}

// Does this question have any reference on file? Drives expanded-by-default vs the none-on-file note.
export function hasGrounding(group) {
  return groundingReferences(group).length > 0;
}

// The "no reference images on file" note wording (R-V4-A9). The "(legacy batch)" parenthetical is
// asserted ONLY when the item is KNOWN legacy (groundingStatus === UNKNOWN_LEGACY, admin-only). A
// post-S3 UNGROUNDED item is NOT legacy — labeling it so would contradict the A2 honesty rationale.
// Tutors, who never receive groundingStatus, therefore get the neutral always-true wording rather
// than a possibly-false legacy claim. Returns null when references exist (the note never shows then).
export function groundingNoteText(group) {
  if (hasGrounding(group)) return null;
  return group && group.groundingStatus === GROUNDING_STATUS.UNKNOWN_LEGACY
    ? "No reference images on file (legacy batch)"
    : "No reference images on file";
}

// Admin-only per-candidate generation-role chips (D1/D5/R-V4-A7): provenance.referenceRoles is a
// {assetId: CONDITIONING|STYLE} map the pipeline DECLARES (labeled "declared by pipeline" in the UI).
// It rides inside the admin-only provenance map, so the redaction choke point strips it for tutors;
// this reader only ever runs inside the admin-gated metadata panel (belt + suspenders). Null-safe.
export function referenceRoleChips(candidate) {
  const roles = (candidate && candidate.provenance && candidate.provenance.referenceRoles) || {};
  return Object.keys(roles).map((assetId) => ({ assetId, role: roles[assetId] }));
}

// --- Keyboard shortcuts (nav + zoom only; verdict digit-picks retired) ---

export const SHORTCUT = {
  PREV: "prev",
  NEXT: "next",
  CLOSE_ZOOM: "closeZoom",
};

// Is a keyboard event's target a text-entry field? Shortcuts stay inert while one
// has focus (the reviewer is typing a note).
export function isEditableTarget(target) {
  if (!target) return false;
  const tag = String(target.tagName || "").toUpperCase();
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return Boolean(target.isContentEditable);
}

// Resolve a keydown into a navigation action, or null.
//   event: { key, ctrlKey, metaKey, altKey, editable }
//   uiState: { lightboxOpen }
// Esc closes the zoom lightbox when open (and while open it traps the keyboard).
// ←/→ navigate questions; inert while typing or with a modifier held. Verdict
// digit-picks and the Enter-submit chord are retired with the tournament UI.
export function resolveShortcut(event, uiState) {
  const e = event || {};
  const ui = uiState || {};
  const key = e.key;
  const anyModifier = Boolean(e.ctrlKey || e.metaKey || e.altKey);

  if (key === "Escape" || key === "Esc") {
    return ui.lightboxOpen ? { type: SHORTCUT.CLOSE_ZOOM } : null;
  }
  if (ui.lightboxOpen) return null;
  if (e.editable || anyModifier) return null;
  if (key === "ArrowLeft") return { type: SHORTCUT.PREV };
  if (key === "ArrowRight") return { type: SHORTCUT.NEXT };
  return null;
}

// --- Queue helpers (retained) ---

export function groupItemIds(group) {
  return (((group && group.candidates) || []).map((c) => c && c.id)).filter(Boolean);
}

export function mergeQueueItems(existing, incoming) {
  const seen = new Set((existing || []).map((it) => it && it.id));
  const merged = [...(existing || [])];
  (incoming || []).forEach((it) => {
    if (it && it.id != null && !seen.has(it.id)) {
      seen.add(it.id);
      merged.push(it);
    }
  });
  return merged;
}

// The session progress line, in QUESTIONS (groups) — the same unit the header and
// rail count in (#584 defect 3).
export function groupProgress(counts) {
  const c = counts || {};
  const reviewed = c.reviewedGroups || 0;
  const toReview = c.toReviewGroups || 0;
  const base =
    `${reviewed} question${reviewed === 1 ? "" : "s"} reviewed this session` +
    ` · ${toReview} to review`;
  return c.moreOnServer ? `${base} · more will load` : base;
}

// --- Metadata (retained: admin-only reference chrome) ---

export function modelInLoopSummary(candidate) {
  if (!candidate) return null;
  const prov = candidate.provenance || {};
  const p = candidate.payload || {};
  const gen = prov.model || p.model || null;
  const verdict =
    p.verificationVerdict || (p.verification && p.verification.verdict) || null;
  const parts = [];
  if (gen) parts.push(`generated by ${gen}`);
  parts.push(
    verdict
      ? `screening model: not recorded (verdict ${verdict})`
      : "screening model: not recorded"
  );
  return parts.join(" · ");
}

export function metadataRows(candidate) {
  if (!candidate) return [];
  const prov = candidate.provenance || {};
  const p = candidate.payload || {};
  const rows = [
    ["Model", prov.model || p.model],
    ["Generation batch", prov.generationBatchId],
    ["Prompt", prov.prompt],
    ["Cost", prov.cost != null ? String(prov.cost) : null],
    ["Candidate", p.candidateId],
    ["Type", p.candidateType],
    // S5/P0-2/A4: per-candidate placement in the admin metadata panel. Because it is rendered per
    // candidate, a group where candidates DISAGREE on placement shows the mix here (the banner
    // itself uses leak-safe STEM dominance and stays a single line).
    ["Placement", p.placement],
    ["Screening", p.verificationVerdict || (p.verification && p.verification.verdict)],
    ["Ingested", candidate.createDt],
    ["Review status", candidate.reviewStatus],
    ["Decided", candidate.reviewDecidedAt],
    ["Decided by", candidate.reviewDecidedBy],
    ["Publish status", candidate.publishStatus],
    ["Published", candidate.publishedAt],
    ["Published by", candidate.publishedBy],
    ["Publish batch", candidate.publishBatchId],
    ["Retracted", candidate.retractedAt],
  ];
  return rows
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([label, value]) => ({ label, value: String(value) }));
}
